import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronsDown, ChevronsUp, Grid2X2, List, Search, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { usePermissionStore } from '@/stores/permission'
import { materialsApi } from '@/api/materials'
import type { MaterialFolder } from '@/types/material'
import { useTheme } from '@/hooks/useTheme'
import { MaterialFolderDialog } from './components/MaterialFolderDialog'
import { MaterialFolderTree } from './components/MaterialFolderTree'
import { MaterialGrid } from './components/MaterialGrid'
import { MaterialTable } from './components/MaterialTable'
import { MaterialUploadDashboard } from './components/MaterialUploadDashboard'

type MaterialView = 'grid' | 'table'

export function MaterialsPage() {
	const queryClient = useQueryClient()
	const hasPermission = usePermissionStore((state) => state.hasPermission)
	const { isDark } = useTheme()
	const [folderId, setFolderId] = useState(0)
	const [folderName, setFolderName] = useState('根目录')
	const [keyword, setKeyword] = useState('')
	const [uploadedDateRange, setUploadedDateRange] = useState<{ start_date?: string; end_date?: string }>({})
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [view, setView] = useState<MaterialView>('grid')
	const [foldersCollapsed, setFoldersCollapsed] = useState(false)
	const [uploadOpen, setUploadOpen] = useState(false)
	const [folderDialog, setFolderDialog] = useState<{
		open: boolean
		parent?: MaterialFolder
		current?: MaterialFolder
	}>({ open: false })

	const foldersQuery = useQuery({
		queryKey: ['material-folders'],
		queryFn: materialsApi.folderTree,
	})
	const materialsQuery = useQuery({
		queryKey: ['materials', folderId, keyword, uploadedDateRange.start_date, uploadedDateRange.end_date, page, pageSize],
		queryFn: () => materialsApi.list(materialListParams({
			folderId,
			keyword,
			uploadedDateRange,
			page,
			pageSize,
		})),
	})

	const deleteMaterialMutation = useMutation({
		mutationFn: materialsApi.delete,
		onSuccess: () => {
			toast.success('素材已删除')
			queryClient.invalidateQueries({ queryKey: ['materials'] })
		},
	})

	const deleteFolderMutation = useMutation({
		mutationFn: materialsApi.deleteFolder,
		onSuccess: () => {
			toast.success('目录已删除')
			queryClient.invalidateQueries({ queryKey: ['material-folders'] })
			queryClient.invalidateQueries({ queryKey: ['materials'] })
			setFolderId(0)
			setFolderName('根目录')
		},
	})

	const handleUploadComplete = useCallback(() => {
		setUploadOpen(false)
		void queryClient.invalidateQueries({ queryKey: ['materials'] })
	}, [queryClient])

	const handleDeleteFolder = (folder: MaterialFolder) => {
		if (!window.confirm(`确认删除目录「${folder.name}」？`)) return
		deleteFolderMutation.mutate(folder.id)
	}

	const closeFolderDialog = (open: boolean) => {
		setFolderDialog((value) => (open ? { ...value, open } : { open: false }))
	}

	return (
		<div className="flex h-[calc(100vh-7rem)] min-h-0 flex-col gap-4 overflow-hidden md:flex-row">
			<aside className="flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden border-b pb-4 md:w-72 md:border-b-0 md:border-r md:pb-0 md:pr-4">
				<div className="mb-3 flex shrink-0 items-center justify-between">
					<h2 className="text-base font-semibold tracking-normal">目录</h2>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						className="h-7 w-7 shrink-0"
						aria-label={foldersCollapsed ? '全部展开' : '全部收起'}
						onClick={() => setFoldersCollapsed((value) => !value)}
					>
						{foldersCollapsed ? <ChevronsDown className="h-4 w-4" /> : <ChevronsUp className="h-4 w-4" />}
					</Button>
				</div>
				<div data-testid="material-folder-scroll" className="min-h-0 flex-1 overflow-y-auto pr-1">
					<MaterialFolderTree
						folders={foldersQuery.data ?? []}
						selectedId={folderId}
						collapsed={foldersCollapsed}
						onSelect={(id, name) => {
							setFolderId(id)
							setFolderName(name)
							setPage(1)
						}}
						onCreate={(parent) => setFolderDialog({ open: true, parent })}
						onEdit={(current) => setFolderDialog({ open: true, current })}
						onDelete={handleDeleteFolder}
					/>
				</div>
			</aside>
			<main className="min-h-0 min-w-0 flex-1 space-y-4 overflow-y-auto">
				<div className="flex flex-wrap items-center gap-2">
					<div className="relative min-w-[240px] flex-1">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							value={keyword}
							onChange={(event) => {
								setKeyword(event.target.value)
								setPage(1)
							}}
							className="pl-8 focus-visible:ring-0 focus-visible:ring-offset-0"
							placeholder="搜索素材名称"
						/>
					</div>
					<DateRangePicker
						value={uploadedDateRange}
						className="h-9"
						onChange={(value) => {
							setUploadedDateRange(value)
							setPage(1)
						}}
					/>
					<PermissionGuard permission="material:upload">
						<Button type="button" onClick={() => setUploadOpen(true)}>
							<Upload className="mr-2 h-4 w-4" />
							上传
						</Button>
					</PermissionGuard>
				</div>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<h1 className="min-w-0 truncate text-lg font-semibold tracking-normal">{folderName}</h1>
					<div className="flex shrink-0 items-center gap-3">
						<span className="text-sm text-muted-foreground">{materialsQuery.data?.total ?? 0} 个素材</span>
						<div className="flex rounded-md border p-0.5">
							<Button
								type="button"
								size="icon"
								variant={view === 'grid' ? 'secondary' : 'ghost'}
								className="h-8 w-8"
								aria-label="宫格视图"
								onClick={() => setView('grid')}
							>
								<Grid2X2 className="h-4 w-4" />
							</Button>
							<Button
								type="button"
								size="icon"
								variant={view === 'table' ? 'secondary' : 'ghost'}
								className="h-8 w-8"
								aria-label="表格视图"
								onClick={() => setView('table')}
							>
								<List className="h-4 w-4" />
							</Button>
						</div>
					</div>
				</div>
				{view === 'grid' ? (
					<MaterialGrid
						materials={materialsQuery.data?.list ?? []}
						canDelete={hasPermission('material:delete')}
						onDownload={(id) => materialsApi.download(id)}
						onDelete={(id) => deleteMaterialMutation.mutate(id)}
					/>
				) : (
					<MaterialTable
						materials={materialsQuery.data?.list ?? []}
						canDelete={hasPermission('material:delete')}
						onDownload={(id) => materialsApi.download(id)}
						onDelete={(id) => deleteMaterialMutation.mutate(id)}
					/>
				)}
				<ListPagination
					total={materialsQuery.data?.total ?? 0}
					page={page}
					pageSize={pageSize}
					loading={materialsQuery.isFetching}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
				/>
			</main>
			<MaterialFolderDialog
				open={folderDialog.open}
				parent={folderDialog.parent}
				current={folderDialog.current}
				onOpenChange={closeFolderDialog}
			/>
			<MaterialUploadDashboard
				open={uploadOpen}
				folderId={folderId}
				folderName={folderName}
				isDark={isDark}
				onOpenChange={setUploadOpen}
				onComplete={handleUploadComplete}
			/>
		</div>
	)
}

function materialListParams({
	folderId,
	keyword,
	uploadedDateRange,
	page,
	pageSize,
}: {
	folderId: number
	keyword: string
	uploadedDateRange: { start_date?: string; end_date?: string }
	page: number
	pageSize: number
}) {
	return {
		folder_id: folderId,
		keyword: keyword.trim(),
		...(uploadedDateRange.start_date ? { start_time: toMaterialStartTime(uploadedDateRange.start_date) } : {}),
		...(uploadedDateRange.end_date ? { end_time: toMaterialEndTime(uploadedDateRange.end_date) } : {}),
		page,
		page_size: pageSize,
	}
}

function toMaterialStartTime(value: string) {
	return value ? `${value} 00:00:00` : undefined
}

function toMaterialEndTime(value: string) {
	return value ? `${value} 23:59:59` : undefined
}
